/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback, useState, useEffect } from "react";

// antd:
import { Steps, Avatar, Space, Button, Modal } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
import DivE from "../react/DivE.tsx";
// import SpanE from "../react/SpanE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getAllApps, getLng, getDict, getTheme, getAppState, changeAppState, getUser, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions, Tuser} from "../../types/easyTypes.ts";
import type {TuiState} from "../../types/uiapp.d.ts";

const { confirm } = Modal;
const { rz, $ } = window;

type Tcomponent = {
  type:"default" | "navigation" | "inline",
  direction:"horizontal" | "vertical",
  labelPlacement:"horizontal" | "vertical",
  size:"default" | "small"
  progressDot:boolean,
  responsive:boolean,
  readOnly?:boolean
};

type TconfirmationMethod = {
  rowkey:string,
  title_ru:string,
  title_en:string,
  title_tr:string,
  needToApprove:boolean
};

type Tstep = {
  user:string,
  status:"inline" | "waiting" | "reject" | "accept" | "message" | "question",
  confirmationMethod:string, // "notification" | "examination" | "approval" | can be more...
  strDate:string
};

const ErrorContent:FC = (msg:string) => (
  <div className="uk-flex uk-flex-center uk-flex-middle" style={{height: "100%"}}>
    <h2 className="uk-margin-remove uk-text-center txt-white txt-stroke-red-shadow">{msg}</h2>
  </div>
);

const getFullPathFromKey = (path:string, appstate:TuiState) => {
  let fullPath = path;
  if (!~fullPath.indexOf("/")) {
    let fnd = false; const allStateKeys = rz.objKeys(appstate);
    for (let i = 0; i < allStateKeys.length; i++) {
      // eslint-disable-next-line prefer-destructuring
      if (appstate[allStateKeys[i]].key === path) { fnd = true; fullPath = appstate[allStateKeys[i]].fullPath; break }
    }
    if (!fnd) { return path }
  }
  return fullPath;
};

const recalcQueue = ({items, item, status, allConfirmationMethods}) => {
  const itms1 = items.map((ii) => ({...ii})); // Clone array and clone children objects
  itms1[item].status = status; itms1[item].strDate = rz.dateToString(new Date(), "YYYY-MM-DD hh:mm:ss");

  for (let i = item; i < itms1.length; i++) {
    const confrmMethod:TconfirmationMethod = (allConfirmationMethods || []).find((mm) => (mm.rowkey === itms1[i].confirmationMethod)) || {rowkey: itms1[i].confirmationMethod, title_ru: "", title_en: "", title_tr: "", needToApprove: false};

    if (i > 0 && itms1[i].status === "inline" && rz.arrContains(["accept", "message"], itms1[i - 1].status)) {
      if (!confrmMethod.needToApprove) { itms1[i].status = "message" }
      else { itms1[i].status = "waiting" }
    }
  }
  return itms1;
};

const RZApproval:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZApproval.displayName = "Approval";
  const dispatch = useAppDispatch();
  const allApps = useAppSelector(getAllApps);
  const user = useAppSelector(getUser);
  const lng = useAppSelector(getLng);
  const theme = useAppSelector(getTheme);
  const dct = useAppSelector(getDict);
  const safe = useAppSelector(getSafePtr);
  // const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key));
  const elState = appState[fullPath];
  const {color, backgroundColor} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes.key || rz.strMakeID(24)]), [rootAttributes.key]);

  const [errMsg, setErrMsg] = useState("");
  // Get all users
  const [allUsers, setAllUsers] = useState<Array<Tuser>>([]);
  useEffect(() => {
    if (allUsers.length || errMsg) { return }
    if (elState.specialParams && elState.specialParams.allUsers) { setAllUsers(elState.specialParams.allUsers); return }
    rz.serverAction({appkey: rootAttributes.key, action: "loadAuxiliaryDBs", params: {dbs: ["users"]}}).then((ans) => {
      // @ts-ignore
      if (ans && ans.users && ans.users.length) { setAllUsers(ans.users) }
      else { setErrMsg(" ") }
    });
  }, [allUsers.length, elState.specialParams, errMsg, rootAttributes.key]);

  // Get user roles
  const userRoles = useMemo<Array<string>>(() => {
    const curApp = (allApps || fnc.memorizedValues.apps).find((aa) => (aa.key === rootAttributes?.key));
    if (!curApp) { return [] }
    return fnc.getUserRolesForApp(user, curApp);
  }, [allApps, rootAttributes?.key, user]);

  // Get approval methods
  const [allConfirmationMethods, setAllConfirmationMethods] = useState<Array<TconfirmationMethod>>([]);
  useEffect(() => {
    if (allConfirmationMethods.length || errMsg) { return }
    if (elState.specialParams && elState.specialParams.confirmationmethods) { setAllConfirmationMethods(elState.specialParams.confirmationmethods); return }
    rz.serverAction({appkey: rootAttributes.key, action: "loadAuxiliaryDBs", params: {dbs: ["pw_confirmation_methods"]}}).then((ans) => {
      // @ts-ignore
      if (ans && ans.pw_confirmation_methods && ans.pw_confirmation_methods.length) { setAllConfirmationMethods(ans.pw_confirmation_methods) }
      else { setErrMsg(" ") }
    });
  }, [allConfirmationMethods.length, elState.specialParams, errMsg, rootAttributes.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const stepsType = useMemo<Tcomponent>(() => {
    const typ:Tcomponent = {type: "default", direction: "horizontal", labelPlacement: (attributes?.progressDot === "true" ? "vertical" : "horizontal"), size: "default", progressDot: false, responsive: true};
    (type || "").toLowerCase().replace((/(\s+|,|\|)/igm), ";").split(";").forEach((tt) => {
      if (~["horizontal", "vertical"].indexOf(tt)) { typ.direction = tt }
      else if (~["horizontallabel", "verticallabel"].indexOf(tt)) { typ.labelPlacement = tt.replace("label", "") }
      else if (~["small", "default"].indexOf(tt)) { typ.size = tt }
      else if (~["navigation", "inline"].indexOf(tt)) { typ.type = tt }
      else if (~["dot", "progressdot"].indexOf(tt)) { typ.progressDot = true }
      else if (tt === "responsive") { typ.responsive = true }
      else if (tt === "readonly") { typ.readOnly = true }
    });

    if (attributes?.size && ~["default", "small"].indexOf(attributes.size)) { typ.size = attributes.size }
    if (attributes?.labelPlacement && ~["horizontal", "vertical"].indexOf(attributes.labelPlacement)) { typ.labelPlacement = attributes.labelPlacement }
    if (attributes?.direction && ~["horizontal", "vertical"].indexOf(attributes.direction)) { typ.direction = attributes.direction }
    if (attributes?.progressDot) { typ.progressDot = (attributes.progressDot === "true") }
    if (attributes?.responsive) { typ.responsive = (attributes.responsive === "true") }
    if (attributes?.readOnly || attributes?.readonly) { typ.readOnly = ((attributes.readOnly || attributes.readonly) === "true") }

    return typ;
  }, [type, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  const openid = useMemo(() => {
    if (elState?.specialParams && elState.specialParams?.openid) { return elState.specialParams.openid }
    const prefix = key.split("-")[0] + "-"; if (!rz.arrContains(["vf-", "ff-", "ef-", "cf-"], prefix)) { return "" }
    const pth = getFullPathFromKey(prefix + "_id", appState); if (!pth || !appState[pth]) { return "" }
    return appState[pth].value || "";
  }, [appState, elState.specialParams, key]);

  const recipients = useMemo<Array<Tuser>>(() => {
    let rcpts:Array<Tuser> = [];
    if (elState?.specialParams && elState.specialParams?.recipients && rz.isArray(elState.specialParams?.recipients)) {
      const uniqueStr = rz.arrGetUnique(elState.specialParams?.recipients);
      rcpts = (allUsers || []).filter((uu) => (rz.arrContains(uniqueStr, uu.login, true, true)));
    }
    else if (attributes.recipients && rz.isString(attributes.recipients)) {
      (attributes.recipients.replace((/(\s+|,|\|)/igm), ";").split(";")).forEach((ff:string) => {
        if (ff === "curUser") {
          if (!~rz.arrFindObj(rcpts, {login: user.login})) { rcpts.push(rz.objClone(user)) }
        }
        else {
          const pth = getFullPathFromKey(ff, appState);
          if (pth && appState[pth]) {
            const el = appState[pth]; let logins:Array<string> = [];
            if (rz.arrContains(["approval", "chat"], el.name) || (el.name === "aggrid" && ~el.key.indexOf("history"))) {
              logins = (el.data || []).map((dd) => (dd.user)).filter((aa) => (!~rz.arrFindObj(rcpts, {login: aa})));
            }
            else if (el.name === "select") {
              if (rz.isArray(el.value)) { logins = el.value.filter((aa) => (!~rz.arrFindObj(rcpts, {login: aa}))) }
              else if (rz.isString(el.value) && !~rz.arrFindObj(rcpts, {login: el.value})) { logins.push(el.value) }
            }
            (allUsers || []).forEach((uu) => { if (rz.arrContains(logins, uu.login, true, true)) { rcpts.push(rz.objClone(uu)) } });
          }
        }
      });
    }
    return rcpts;
  }, [elState.specialParams, attributes.recipients, allUsers, user, appState]);

  const stageButtonOnClick = useCallback((step:Tstep, item:number, status:string) => {
    if (!step || !~item || !rz.arrContains(["accept", "question", "reject"], status)) { return }
    confirm({
      title: dct.p.standartQuestion, icon: <AppIcons icopath="ai/AiOutlineExclamationCircle" attrs={{}} />,
      content: rz.switchCase([
        status === "accept", rz.strFromSelectedLng({lng: (lng as string), ru: "Утвердить документ?", en: "Approve the document?", tr: "Belgeyi onaylamak?"}),
        status === "question", rz.strFromSelectedLng({lng: (lng as string), ru: "Задать вопрос / Запросить комментарий в чате", en: "Ask a question / Request a commentary in the chat", tr: "Bir soru sorun / sohbette bir yorum isteyin"}),
        rz.strFromSelectedLng({lng: (lng as string), ru: "Отклонить документ?", en: "Reject the document?", tr: "Belgeyi reddediyor mu?"})
      ]),
      onOk() {
        const newItems = recalcQueue({items: (elState?.data || []), item, status, allConfirmationMethods});
        // console.log({newItems});
        // @ts-ignore
        dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {data: newItems}}));

        const attr1 = (attributes?.onChange || attributes?.onchange);
        if (attr1 && rootNamespace && rootNamespace[attr1]) {
          const clientFNC = rootNamespace[attr1];
          const recipients1 = rz.arrGetUnique((recipients || []).map((ii) => (ii.login || "")), true, true, true);

          if (rz.isPromise(clientFNC)) {
            clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, approval: newItems, openid, recipients: recipients1, safe}).then((ret) => {
              if (ret && ret.err) { console.log(ret.err) }
              if (ret && ret?.state?.fullPath && ret?.state?.newState) {
                dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
              }
            }).catch((err) => { console.log(err) });
          }
          else if (rz.isFunction(clientFNC)) {
            const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, approval: newItems, openid, recipients: recipients1, safe});
            if (ret && ret.err) { console.log(ret.err) }
            if (ret && ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }
      }
    });
  }, [dct, lng, elState?.data, allConfirmationMethods, dispatch, rootAttributes.key, fullPath, attributes, rootNamespace, recipients, openid, key, user, safe]);

  const getButtonsForOneStage = useCallback((step:Tstep, item:number) => {
    if (stepsType.readOnly || (user.login !== step.user && !rz.arrCompare(["admins"], userRoles, "includesAny")) || !rz.arrContains(["waiting", "question", "reject"], step.status)) { return "" }
    const btns = [
      (<Button key={key + "-btn-accept"} id="accept" color="cyan" variant="solid" shape="circle" className="rz-child-flex rz-child-flex-middle" onClick={() => { stageButtonOnClick(step, item, "accept") }}
        icon={<AppIcons key={key + "-btn-accept-ico"} icopath="ai/AiOutlineCheckCircle" attrs={{ size: "2em", "uk-tooltip": rz.strFromSelectedLng({lng: (lng as string), ru: "Утвердить", en: "Approve", tr: "Onaylamak"}) }} />}
      />)
    ];
    if (step.status !== "question") {
      btns.push(<Button key={key + "-btn-question"} id="question" color="primary" variant="solid" shape="circle" className="rz-child-flex rz-child-flex-middle" onClick={() => { stageButtonOnClick(step, item, "question") }}
        icon={<AppIcons key={key + "-btn-question-ico"} icopath="ai/AiOutlineQuestionCircle" attrs={{ size: "2em", "uk-tooltip": rz.strFromSelectedLng({lng: (lng as string), ru: "Вопрос", en: "Question", tr: "Soru"}) }} />}
      />);
    }
    if (step.status !== "reject") {
      btns.push(<Button key={key + "-btn-reject"} id="reject" color="danger" variant="solid" shape="circle" className="rz-child-flex rz-child-flex-middle" onClick={() => { stageButtonOnClick(step, item, "reject") }}
        icon={<AppIcons key={key + "-btn-reject-ico"} icopath="ai/AiOutlineCloseCircle" attrs={{ size: "2em", "uk-tooltip": rz.strFromSelectedLng({lng: (lng as string), ru: "Отклонить", en: "Reject", tr: "Reddetmek"}) }} />}
      />);
    }

    return (<Space key={key + "-description-buttons-space"} size="small" wrap={true}>{btns}</Space>);
  }, [stepsType.readOnly, user.login, userRoles, key, lng, stageButtonOnClick]);

  const aprItems = useMemo(() => {
    // elState?.data = {user, status, confirmationMethod, strDate, comment}
    const itms = (elState?.data || []).map((ii, i) => {
      const confrmMethod:TconfirmationMethod = allConfirmationMethods.find((mm) => (mm.rowkey === ii.confirmationMethod)) || {rowkey: ii.confirmationMethod, title_ru: "", title_en: "", title_tr: "", needToApprove: false};
      // Some correction (if User edited the db.writs manually)
      const jj = rz.objClone(ii);
      if (i === 0 && rz.arrContains(["", "inline"], jj.status)) {
        if (rz.arrContains(["examination", "approval"], jj.confirmationMethod)) { jj.status = "waiting" }
        else if (rz.arrContains(["notification", "message", "info"], jj.confirmationMethod)) { jj.status = "message" }
      }
      // @ts-ignore
      const usr = (allUsers.find((uu) => (uu.login.toLowerCase() === (jj.user || "").toLowerCase())) || {login: jj.user, name_ru: "", name_en: ""});

      return {
        key: key + "-" + (i + 1),
        icon: (<AppIcons key={key + "-ico-" + (i + 1)} icopath={rz.switchCase([
          jj.status === "inline", "gr/GrDocumentLocked",
          jj.status === "waiting", "gr/GrDocumentTime",
          jj.status === "reject", "ai/AiOutlineCloseCircle",
          jj.status === "accept", "ai/AiOutlineCheckCircle",
          jj.status === "message", "fa/FaTelegramPlane",
          jj.status === "question", "ai/AiOutlineQuestionCircle",
          "md/MdErrorOutline"
        ])} attrs={{ size: "2em", color: rz.switchCase([
          jj.status === "inline", "Gray|Lightgrey",
          jj.status === "waiting", "Royalblue|Lightskyblue",
          jj.status === "reject", "Tomato|Salmon",
          jj.status === "accept", "Seagreen|Lightgreen",
          jj.status === "message", "Steelblue|Skyblue",
          jj.status === "question", "Sandybrown",
          "Gray|Lightgrey"
        ]), "uk-tooltip": rz.switchCase([
          jj.status === "inline", rz.strFromSelectedLng({lng: (lng as string), ru: "В очереди", en: "In line", tr: "Çizgide"}),
          jj.status === "waiting", rz.strFromSelectedLng({lng: (lng as string), ru: "На рассмотрении", en: "On consideration", tr: "Dikkate alınarak"}),
          jj.status === "reject", rz.strFromSelectedLng({lng: (lng as string), ru: "Отклонено", en: "Rejected", tr: "Reddedilmiş"}),
          jj.status === "accept", rz.strFromSelectedLng({lng: (lng as string), ru: "Согласовано", en: "Accepted", tr: "Kabul edilmiş"}),
          jj.status === "message", rz.strFromSelectedLng({lng: (lng as string), ru: "Отправлено информационное сообщение", en: "Sent an information message", tr: "Bir bilgi mesajı gönderdi"}),
          jj.status === "question", rz.strFromSelectedLng({lng: (lng as string), ru: "Вопрос в связанном чате", en: "There is a question in the related chat", tr: "İlgili sohbette bir soru var"}),
          rz.strFromSelectedLng({lng: (lng as string), ru: "Ошибка", en: "An Error", tr: "Bir hata"})
        ]) }}
        />),
        title: (<DivE key={key + "-title-wrapper-" + (i + 1)} className="rz-margin-left-right-5 uk-flex uk-flex-middle">
          <Avatar key={key + "-avatar-" + (i + 1)} size="large" shape="square"
            src={"/ui/avatar/" + jj.user} icon={<AppIcons icopath="fa/FaUserSecret" attrs={{}} />}
            uk-tooltip={rz.strFromSelectedLng({lng: (lng as string), ru: (usr.name_ru as string), en: (usr.name_en as string), def: usr.login})}
          />
        </DivE>),
        description: (<DivE key={key + "-description-wrapper-" + (i + 1)}>
          <DivE key={key + "-description-text-" + (i + 1)}>{
            rz.strFromSelectedLng({
              lng: (lng as string),
              ru: (confrmMethod.needToApprove ? "Требуется: " : "Действие: "),
              en: (confrmMethod.needToApprove ? "Required: " : "Action: "),
              tr: (confrmMethod.needToApprove ? "Gerekli: " : "Diğer hareket: ")
            }) + rz.strFromSelectedLng({lng: (lng as string), ru: confrmMethod.title_ru, en: confrmMethod.title_en, tr: confrmMethod.title_tr, def: jj.confirmationMethod})
          }</DivE>
          <DivE key={key + "-description-buttons-wrapper-" + (i + 1)}>{
            getButtonsForOneStage(jj, i)
          }</DivE>
        </DivE>),
        // wait process finish error
        status: rz.switchCase([
          rz.arrContains(["waiting", "question"], jj.status), "process",
          rz.arrContains(["accept", "message"], jj.status), "finish",
          rz.arrContains(["reject"], jj.status), "error",
          "wait" // jj.status === "inline"
        ])
      };
    });

    if (itms.length === 1) {
      itms.push({
        key: key + "-" + (itms.length + 1),
        icon: (<AppIcons key={key + "-ico-" + (itms.length + 1)} icopath={
          rz.arrContains(["message", "accept"], itms[itms.length - 1].status)
            ? "ai/AiOutlineCheckCircle"
            : "gr/GrDocumentLocked"
          } attrs={{ size: "2em", color: (
            rz.arrContains(["message", "accept"], itms[itms.length - 1].status)
              ? "Seagreen|Lightgreen"
              : "Gray|Lightgrey"
          ), "uk-tooltip": (
            rz.arrContains(["message", "accept"], itms[itms.length - 1].status)
              ? rz.strFromSelectedLng({lng: (lng as string), ru: "Готово", en: "Ready", tr: "Tamamlamak"})
              : rz.strFromSelectedLng({lng: (lng as string), ru: "В очереди", en: "In line", tr: "Çizgide"})
          ) }}
        />),
        title: (<DivE key={key + "-title-wrapper-" + (itms.length + 1)}>{""}</DivE>),
        description: (<DivE key={key + "-description-text-" + (itms.length + 1)} className="rz-margin-left-right-5 uk-flex uk-flex-middle uk-flex-middle">{
          rz.strFromSelectedLng({lng: (lng as string), ru: "Готово", en: "Ready", tr: "Tamamlamak"})
        }</DivE>),
        status: (
          rz.arrContains(["message", "accept"], itms[itms.length - 1].status)
            ? "finish"
            : "wait"
        )
      });
    }

    return itms;
  }, [elState?.data, allConfirmationMethods, allUsers, key, lng, getButtonsForOneStage]);

  return (
    errMsg
      ? ErrorContent(errMsg)
      : (aprItems && aprItems.length ? (
        <Steps key={key} className={clsu.join(" ")} style={styl}
          type={attributes?.stepstype || stepsType.type} // "navigation" || "inline" || "default"
          size={attributes?.size || stepsType.size} // "default" || "small"
          responsive={stepsType.responsive}
          direction={((stepsType.responsive && aprItems.length > 4) || window.innerWidth < 768) ? "vertical" : stepsType.direction} // "horizontal" || "vertical"
          labelPlacement={stepsType.labelPlacement}
          progressDot={stepsType.progressDot} // labelPlacement will be vertical
          // onChange={activeStepChange}
          items={aprItems}
        />
      ) : ErrorContent(rz.strFromSelectedLng({lng: (lng as string), ru: "Не указаны пользователи, согласующие документ", en: "Users who coordinate the document are not specified", tr: "Belgeyi koordine eden kullanıcılar belirtilmedi"})))
  );
}

export default RZApproval;
/* eslint-enable @typescript-eslint/no-unused-vars */
